import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { X, MessageCircle, ChevronLeft, Boxes, Factory, Search } from "lucide-react";
import WhereIsMyPartTrace from "./WhereIsMyPartTrace";

/* ============== Factory Assistant — floating helper ==============
   Two read-only features behind one bubble:
     📦 Inventory Issues — the existing negative-stock guide (unchanged).
     🏭 Factory Health  — a manager's morning summary, composed ENTIRELY from
        existing endpoints (dashboard stats, rework pool, jobwork snapshot,
        FG snapshot) fetched as the logged-in user, so RBAC applies per row
        and there is no second calculation engine anywhere. A row whose
        source the user cannot access shows "Data unavailable". */
export default function InventoryAssistant({ unitId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [shownIssues, setShownIssues] = useState(15);   // "See more" grows this
  const [view, setView] = useState("menu");        // menu | issues | health | where
  const [data, setData] = useState(null);          // negative-issues feed
  const [health, setHealth] = useState(null);      // {stats, rework, jobwork, fg} — lazy
  const panelRef = useRef(null);

  /* ================================================================
     ROUND CQ — the ball follows whatever you are keying into.
     The assistant used to rove around on its own timer, wherever you
     happened to be working. Now: focus ANY entry field anywhere in the
     app and the ball rolls down the right-hand edge, STOPS level with
     it, and turns its eyes toward the exact field. Leave the field and
     it rolls home and resumes roving.

     Everything is measured live from the DOM, so it works on every
     sheet, card form and dialog with no per-page wiring: table rows
     (production, machine shop, job work, BOP…) are followed by ROW, and
     anything without a row — dialog fields, card forms, filters — is
     followed by the field itself.

     It must never get in the way of the typing, so three rules hold
     while it is following:
       · it dodges — if the field reaches into the ball's column it
         parks just above (or below) that row instead of on top of it;
       · it is click-through (pointer-events: none in CSS), so a stray
         overlap can still never swallow a click or a keystroke;
       · it leaves the tab order, so Tab during entry never lands on it.
     The rove/jump animations are CSS, and a CSS animation beats an
     inline style, so following also switches them off via a class —
     otherwise the ball would refuse to move.
     ================================================================ */
  const ballRef = useRef(null);
  const dyRef = useRef(0);
  const homeRef = useRef(null);                 // pending "rolled home" timer
  const [follow, setFollow] = useState(null);   // {dy} | null

  /* ================================================================
     ROUND DF — the EYES NO LONGER MOVE. Owner's call: the ball may move,
     its eyes may not. Two separate behaviours were doing the moving and
     both are gone:

       · the page-open tour, where both pupils leapt out of the ball and
         flew around the viewport on their own spline paths;
       · the look-at shift, where the sockets tracked whatever field was
         being keyed into (--eye-x / --eye-y).

     What is LEFT is all ball motion: the flight, the hover, the glow, the
     per-page sizing, and the roll down the right edge to the row being
     keyed. The eyes still blink — a blink is not travel, and a frozen
     stare reads as switched off.

     The free-space probe below survives because the BALL still needs it:
     it sizes the ball and plans every flight.
     ================================================================ */

  /* ================================================================
     ROUND CS — per-page sizing, from the room the page actually has.

     The first version was fixed CSS keyframes: identical every time,
     too fast, and blind to what was underneath it. This measures the
     page instead:

       1. LOOK — sample a grid of points across the viewport with
          elementFromPoint and mark which are free (nothing but page
          background underneath).
       2. SIZE — scale the ball to the page's own clearance, so where
          there is little room it sits small instead of barging over
          the content.
       3. FLY — the same map is what every flight is planned and checked
          against, so an airy dashboard gets a wide crossing and a dense
          entry sheet gets a short hop into whatever gap it has.

     Never runs while the ball is following a field (Round CQ), and
     prefers-reduced-motion skips it entirely.
     ================================================================ */
  const routeLoc = useLocation();
  const followRef = useRef(null);
  const animsRef = useRef([]);
  const [ballScale, setBallScale] = useState(1);
  const [flying, setFlying] = useState(false);
  const openRef = useRef(false);                  // panel open? don't fly away
  followRef.current = follow;
  openRef.current = open;

  const guardRef = useRef(null);                  // live footprint watchdog
  const stopRoll = () => {
    if (guardRef.current) { clearInterval(guardRef.current); guardRef.current = null; }
    animsRef.current.forEach((a) => { try { a.cancel(); } catch {} });
    animsRef.current = [];
    setFlying(false);
  };

  useEffect(() => {
    const btn = ballRef.current;
    if (!btn || followRef.current) return;
    if (typeof btn.animate !== "function") return;              // very old browser
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;

    const rnd = (a, b) => a + Math.random() * (b - a);
    let _idle = null, _timer = null, cancelled = false;

    /* ROUND DI — ONE flight system, and it never stops.

       The ball used to have TWO kinds of motion: this planned trip, and a
       fixed CSS keyframe wander (assistantRove) that filled the gaps between
       trips. The wander was never checked against anything — it just
       translated up-and-left by a scaled amount — and it is what parked the
       ball on top of a production row. It is gone. Every bit of travel the
       ball now does is planned against the page's own free space and checked
       the same way, and the gaps are filled by simply planning the next
       flight. So it keeps drifting, and it keeps drifting over blank screen.

       Each flight re-measures first: the page it was planned against three
       seconds ago may have finished loading since. */
    const scheduleNext = (ms) => {
      if (cancelled) return;
      _timer = setTimeout(() => {
        if (cancelled) return;
        if (typeof window.requestIdleCallback === "function") {
          _idle = window.requestIdleCallback(flight, { timeout: 1200 });
        } else {
          flight();
        }
      }, ms);
    };

    const flight = () => {
    if (cancelled) return;
    // Never fly out from under the user: not while it is following a field,
    // and not while its own panel is open and anchored to it.
    if (followRef.current || openRef.current) { scheduleNext(3000); return; }

    /* --- STEP 1: where is this page actually empty? ---------------
       Probe a grid with elementFromPoint and keep BOTH answers: the free
       fraction (which sizes the ball and the idle rove) and the per-cell
       occupancy map, which is what the journey below steers by. */
    const W = document.documentElement.clientWidth;
    const H = window.innerHeight;
    const COLS = 14;
    const ROW_STEP = 70;
    const colX = (c) => 60 + (c * (W - 140)) / (COLS - 1);
    /* What counts as "not blank". .aampl-3d covers every <Card>, but panels
       built from the utility classes directly (.stat-card, bare .bg-card) are
       just as solid and were reading as FREE — the probe only saw a blocker
       where it happened to land on the icon or the table inside them, so the
       ball could drift across a card's body. Both are listed now. */
    const BLOCKING = ".aampl-3d, .stat-card, .bg-card, table, button, a, input," +
                     " select, textarea, [role='dialog'], header, nav, aside, img, svg";
    /* HARD PROBE BUDGET — this is the fix for the production sheet freezing.
       Measured on a real sheet (243 operations x 31 day columns = 7,533 inputs):
       one elementFromPoint costs ~0.24ms there, because hit-testing has to walk
       a huge DOM. The route planner below could run 17 candidate routes x ~810
       probes each = ~13,800 calls — about 3.3 SECONDS of blocked main thread,
       and it repeated every few seconds as the next flight was scheduled. On an
       airy page the same planning is a few hundred calls and finishes in ~50ms,
       which is why it never showed up anywhere else.

       So the planner now gets a fixed allowance. Once it is spent, every
       further probe reports "blocked", which makes the candidate fail, the
       search end, and the ball simply stay home until the next attempt. A dense
       entry sheet is exactly the page with nowhere to fly anyway. */
    const PROBE_BUDGET = 700;                   // ~170ms worst case on that sheet
    let probeCount = 0;
    const freeAt = (x, y) => {
      if (probeCount >= PROBE_BUDGET) return false;
      probeCount++;
      const el = document.elementFromPoint(x, y);
      if (!el) return false;
      if (btn.contains(el)) return true;
      return !el.closest(BLOCKING);
    };
    let probes = 0, probesFree = 0;
    const rowY = [];
    for (let ly = 90; ly < H - 90; ly += ROW_STEP) rowY.push(ly);
    const free = [];                       // free[r][c]
    rowY.forEach((y, r) => {
      free[r] = [];
      for (let c = 0; c < COLS; c++) {
        const ok = freeAt(colX(c), y);
        free[r][c] = ok;
        probes++;
        if (ok) probesFree++;
      }
    });
    const pageFree = probes ? probesFree / probes : 1;
    const scale = Number(Math.max(0.6, Math.min(1, 0.6 + pageFree * 0.4)).toFixed(2));
    setBallScale(scale);


    /* --- STEP 2: the section-change journey ----------------------
       ROUND DH. The owner wants the ball to take a slow trip across the screen
       on EVERY section change, and to stay on blank background the whole way.

       "Stay on blank" is not a hope here, it is checked. The occupancy map
       above is the only thing the path is allowed to touch:

         · the grid picks candidate STOPS — cells that are free with a row of
           clearance above and below;
         · the finished curve is then walked and the ball's FOOTPRINT — not its
           centre — is asked of the DOM directly at each step. Measured: a
           centre-only test against the grid let 6 frames in 60 clip a card,
           because the cells are ~100x70 and the ball is 64px across, so it can
           sit centred in a free cell with its shoulder over content. Probing
           centre + four edge points kills that whole class;
         · up to 10 candidate routes are drawn and the first clean one wins. If
           a page is wall-to-wall content and none of them come back clean, the
           ball simply stays home. Better no trip than a trip over a sheet. */
    const isFree = (c, r) => !!(free[r] && free[r][c]);
    // room for the ball itself: this cell plus a row of clearance either side
    const fitsBall = (c, r) =>
      isFree(c, r) && (isFree(c, r - 1) || r === 0) && (isFree(c, r + 1) || r === free.length - 1);

    // the ball's resting centre: the portal pins it bottom-5/right-5 (20px)
    const bw = btn.offsetWidth || 64, bh = btn.offsetHeight || 64;
    const homeX = W - 20 - bw / 2;
    const homeY = H - 20 - bh / 2;

    // The precise test. R is the ball's own radius at the size this page gave
    // it, plus a hair of margin.
    const R = (bw * scale) / 2 + 3;
    const D = R * 0.71;                           // the diagonals of that circle
    const footprintFree = (x, y) =>
      freeAt(x, y) &&
      freeAt(x - R, y) && freeAt(x + R, y) && freeAt(x, y - R) && freeAt(x, y + R) &&
      freeAt(x - D, y - D) && freeAt(x + D, y - D) &&
      freeAt(x - D, y + D) && freeAt(x + D, y + D);
    /* Sampling is by DISTANCE, not by a fixed count. Measured with 36 evenly
       spaced samples: consecutive checks were 50-100px apart on a long route,
       and the ball slipped clean over a card BETWEEN two of them — 7 frames in
       60 on a card grid whose gutters (24px) are narrower than the ball (64px),
       so a probe can sit in a gutter and report free. One check every ~22px of
       travel closes that gap: the ball cannot cross anything card-sized
       unnoticed. It is ~5 elementFromPoint calls per step, in an idle callback,
       and it bails on the first failure. */
    const pathIsClear = (pts) => {
      let walked = 0;
      for (let i = 0; i < pts.length; i++) {
        if (i > 0) walked += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
        if (i > 0 && i < pts.length - 1 && walked < 22) continue;
        walked = 0;
        const x = homeX + pts[i].x, y = homeY + pts[i].y;
        // Home is the ball's own parking spot — it sits there all day whatever
        // is underneath, so the launch and the landing are not "travelling
        // over content" and are not judged as such.
        if (Math.hypot(x - homeX, y - homeY) < 45) continue;
        if (x < R || y < R || x > W - R || y > H - R) return false;   // off-screen
        if (!footprintFree(x, y)) return false;
      }
      return true;
    };

    // every cell the ball could legally stand in, bucketed into a 3x3 map of
    // the viewport so a trip visits genuinely different parts of the screen
    const CELLS = 3;
    const buckets = new Map();
    rowY.forEach((y, r) => {
      for (let c = 0; c < COLS; c++) {
        if (!fitsBall(c, r)) continue;
        const x = colX(c);
        const k = Math.min(CELLS - 1, Math.floor((x / W) * CELLS)) +
                  CELLS * Math.min(CELLS - 1, Math.floor((y / H) * CELLS));
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k).push({ x, y });
      }
    });

    const pick = (a) => a[Math.floor(Math.random() * a.length)];
    const shuffled = (a) => a.map((v) => ({ v, k: Math.random() }))
                             .sort((x, y2) => x.k - y2.k).map((o) => o.v);

    /* Centripetal Catmull-Rom. The stops are CONTROL points, not the animation:
       the Web Animations API interpolates linearly, so flying the stops
       directly puts a visible kink at every one. Centripetal (alpha = 0.5)
       rather than the textbook uniform parametrisation — uniform forms a cusp,
       i.e. a literal 180° reversal, wherever a short interval sits next to a
       long one. Ends are reflected, not duplicated: a duplicated end gives a
       zero-length knot interval and divides by zero here. */
    const spline = (ctrl) => {
      if (ctrl.length < 3) return ctrl;
      const refl = (a, b) => ({ x: 2 * a.x - b.x, y: 2 * a.y - b.y });
      const n = ctrl.length;
      const P = [refl(ctrl[0], ctrl[1]), ...ctrl, refl(ctrl[n - 1], ctrl[n - 2])];
      const knot = (a, b) => Math.sqrt(Math.hypot(b.x - a.x, b.y - a.y)) || 1e-4;
      const out = [];
      for (let i = 0; i + 3 < P.length; i++) {
        const p = [P[i], P[i + 1], P[i + 2], P[i + 3]];
        const t = [0];
        for (let k = 1; k < 4; k++) t.push(t[k - 1] + knot(p[k - 1], p[k]));
        const segLen = Math.hypot(p[2].x - p[1].x, p[2].y - p[1].y);
        const per = Math.max(8, Math.min(60, Math.round(segLen / 6)));
        for (let k = 0; k < per; k++) {
          const tt = t[1] + (t[2] - t[1]) * (k / per);
          const lerp = (a, b, ta, tb) => {
            const w = (tt - ta) / ((tb - ta) || 1e-6);
            return { x: a.x + (b.x - a.x) * w, y: a.y + (b.y - a.y) * w };
          };
          const A1 = lerp(p[0], p[1], t[0], t[1]);
          const A2 = lerp(p[1], p[2], t[1], t[2]);
          const A3 = lerp(p[2], p[3], t[2], t[3]);
          out.push(lerp(lerp(A1, A2, t[0], t[2]), lerp(A2, A3, t[1], t[3]), t[1], t[2]));
        }
      }
      out.push(ctrl[n - 1]);
      return out;
    };

    stopRoll();
    const allStops = [...buckets.values()].flat();
    // nearest-first, for the short-hop fallbacks at the end
    const byNearest = allStops.slice().sort(
      (a, b) => Math.hypot(a.x - homeX, a.y - homeY) - Math.hypot(b.x - homeX, b.y - homeY));

    const tryRoute = (stops, minGap = 170) => {
      // stops that sit almost on top of each other make the curve double back,
      // which is the one thing a spline cannot smooth away
      const keep = [];
      let from = { x: homeX, y: homeY };
      stops.forEach((s) => {
        if (Math.hypot(s.x - from.x, s.y - from.y) > minGap) { keep.push(s); from = s; }
      });
      if (!keep.length) return null;
      const ctrl = [{ x: 0, y: 0 },
                    ...keep.map((s) => ({ x: s.x - homeX, y: s.y - homeY })),
                    { x: 0, y: 0 }];             // always comes home
      const candidate = spline(ctrl);
      /* ROUND DI — a slow vertical SWAY, baked into the path BEFORE the
         clearance check so the ball can never float into something the plain
         curve had cleared. This is what makes the travel read as gliding
         through air rather than being dragged along a line. It tapers to zero
         at both ends, so it leaves home and lands back on it cleanly. */
      const waves = rnd(1.4, 2.4), amp = rnd(5, 11), phase = rnd(0, Math.PI * 2);
      const lastI = candidate.length - 1;
      const swayed = candidate.map((p, i) => {
        const t = lastI ? i / lastI : 0;
        return {
          x: p.x,
          y: p.y + Math.sin(phase + t * Math.PI * 2 * waves) * amp * Math.sin(Math.PI * t),
        };
      });
      return pathIsClear(swayed) ? swayed : null;
    };

    /* The owner's ask is that a trip happens EVERY time, so the search gives
       ground before it gives up: a grand tour of 2-4 regions first, then a
       single far stop, then the nearest clean spot to home. A dense entry
       sheet that cannot host the tour can almost always host the short hop,
       and only a genuinely wall-to-wall screen ends with the ball staying put.
       What never gives ground is pathIsClear — every one of these is checked
       the same way. */
    let pts = null;
    const haveBudget = () => probeCount < PROBE_BUDGET;
    if (allStops.length) {
      const regionKeys = [...buckets.keys()];
      for (let attempt = 0; attempt < 5 && !pts && haveBudget(); attempt++) {
        const regions = shuffled(regionKeys);
        const want = Math.min(regions.length, Math.round(rnd(2, 4)));
        pts = tryRoute(regions.slice(0, want).map((k) => pick(buckets.get(k))));
      }
      for (let attempt = 0; attempt < 4 && !pts && haveBudget(); attempt++) {
        pts = tryRoute([pick(allStops)]);         // one stop, anywhere clean
      }
      for (let i = 0; i < Math.min(8, byNearest.length) && !pts && haveBudget(); i++) {
        pts = tryRoute([byNearest[i]]);           // a short hop into the nearest gap
      }
    }

    /* Last resort on a packed screen. Measured on a sheet that is cards on top
       and a full-width table underneath: not one grid cell had the clearance
       for a stop, so the ball sat dead still for 45s — technically correct,
       and not what was asked for. A short breath out from the corner and back
       is almost always available even there, and it goes through exactly the
       same clearance check as everything else. */
    if (!pts) {
      const dirs = shuffled([[-1, 0], [-1, -0.45], [0, -1], [-0.7, -0.7], [-0.9, 0.25]]);
      for (const [dx, dy] of dirs) {
        if (pts || !haveBudget()) break;
        for (const dist of [170, 135, 105]) {
          pts = tryRoute([{ x: homeX + dx * dist, y: homeY + dy * dist }], 80);
          if (pts || !haveBudget()) break;
        }
      }
    }
    // Nowhere clean on this screen right now. Don't give up for good — the
    // page may still be loading, or the user may scroll something out of the
    // way — just try again in a little while.
    if (!pts) { scheduleNext(rnd(5000, 9000)); return; }

    /* --- STEP 3: fly it, SLOWLY -----------------------------------
       ROUND DI — NO ROTATION. The travel used to spin the ball by the distance
       it had covered, which is how a wheel behaves running along a surface;
       the owner wants it moving the way something flies across open sky. The
       spin is gone, and what carries the character instead is the swayed curve
       above plus one long ease over the whole path.

       Offsets stay distance-proportional, so the ball holds ONE even speed the
       whole way instead of racing the long legs. ~80-110 px/s: a drift. */
    let total = 0;
    const seg = pts.map((p, i) => {
      if (i === 0) return 0;
      const d = Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y) + 2;
      total += d;
      return d;
    });
    let cum = 0, last = -1;
    const frames = pts.map((p, i) => {
      cum += seg[i];
      let off = i === 0 ? 0 : i === pts.length - 1 ? 1 : cum / total;
      if (off <= last) off = Math.min(0.9995, last + 0.0002);   // must increase
      last = off;
      return {
        transform: `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px)`,
        offset: Number(off.toFixed(4)),
        easing: "linear",            // the curve carries the shape; one ease below
      };
    });
    frames[frames.length - 1].offset = 1;

    const speed = rnd(80, 110);                   // px per second — slow on purpose
    const anim = btn.animate(frames, {
      // capped at 14s: the ball is click-through for the whole trip, and a
      // longer one than this starts to feel like the assistant is unavailable
      duration: Math.max(6000, Math.min(14000, (total / speed) * 1000)),
      delay: 240,
      iterations: 1,
      fill: "none",
      easing: "cubic-bezier(.4,0,.35,1)",         // eases out of and back into home
    });
    const list = [anim];
    animsRef.current = list;
    setFlying(true);

    /* WATCHDOG — the free-space map is a SNAPSHOT, and half this app's screens
       fill their cards in after the fetches land. Measured on a page that grows
       mid-flight: the route was clean when it was planned and 13 frames in 60
       were over a card by the time it flew. So the footprint is re-checked
       against the LIVE dom four times a second, and the moment the page grows
       underneath it the ball gives up the trip and hurries home rather than
       finishing a route that is no longer empty. */
    guardRef.current = setInterval(() => {
      const r = btn.getBoundingClientRect();
      const x = r.left + r.width / 2, y = r.top + r.height / 2;
      if (Math.hypot(x - homeX, y - homeY) < 80) return;   // at home, nothing to judge
      if (footprintFree(x, y)) return;
      clearInterval(guardRef.current); guardRef.current = null;
      try { anim.cancel(); } catch {}
      const home = btn.animate(
        [{ transform: `translate(${(x - homeX).toFixed(1)}px, ${(y - homeY).toFixed(1)}px)` },
         { transform: "translate(0px, 0px)" }],
        { duration: 420, easing: "cubic-bezier(.4,0,.2,1)", fill: "none" });
      animsRef.current = list;                    // keep the same identity token
      list[0] = home;
      Promise.allSettled([home.finished]).then(() => {
        if (animsRef.current !== list) return;
        animsRef.current = [];
        setFlying(false);
        // it was chased off that route; give the page longer to settle
        scheduleNext(rnd(6000, 10000));
      });
    }, 250);

    // Only the batch that is STILL current may clear the flag. React runs
    // effects twice in development: the first batch gets cancelled, and a
    // cancelled animation's `finished` promise settles immediately — so its
    // handler would otherwise switch flying back off a frame after the
    // second batch switched it on.
    Promise.allSettled([anim.finished]).then(() => {
      if (list[0] !== anim) return;               // the watchdog took over
      if (guardRef.current) { clearInterval(guardRef.current); guardRef.current = null; }
      if (animsRef.current !== list) return;      // superseded by a newer batch
      animsRef.current = [];
      setFlying(false);
      scheduleNext(rnd(2800, 6500));              // rest a moment, then go again
    });

    };   // end flight()

    // Cursor-follow (effect below) now drives the ball's idle motion, so the
    // autonomous flight is stood down — the two must never both drive the
    // transform. Flip this to re-enable the self-planned flight instead.
    const CURSOR_FOLLOW = true;
    if (!CURSOR_FOLLOW) {
      if (typeof window.requestIdleCallback === "function") {
        // timeout is the backstop: a page that never goes idle still gets
        // measured, just a little later.
        _idle = window.requestIdleCallback(flight, { timeout: 1400 });
      } else {
        _timer = setTimeout(flight, 700);
      }
    }
    return () => {
      cancelled = true;
      if (_idle != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(_idle);
      }
      if (_timer) clearTimeout(_timer);
      stopRoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLoc.pathname]);

  /* Cursor following REMOVED (owner's call). The ball used to chase the mouse
     on every mousemove via a permanent requestAnimationFrame loop that wrote
     btn.style.transform each frame. Two reasons it is gone and not just
     disabled: it made the ball wander the screen while you worked, and that
     per-frame inline write OWNED the transform — it silently overrode both the
     row-following translate (Round CQ) and the CSS idle rove, so whatever ran
     last won. With it deleted the transform has exactly one writer again:
     React's inline style while following a field, and the CSS animation
     otherwise. The ball now stays in its corner unless a field pulls it. */

  useEffect(() => {
    // Clearance kept between the ball and the line being keyed. 10px was close
    // enough that the ball read as sitting ON the row; this is a visible gap.
    const GAP = 26;

    /* Where the ball must NOT follow: its own panel (it would chase itself),
       the global nav search, and anything a page opts out of with
       data-no-assistant. Everything else that takes typing counts as entry. */
    const SKIP = '[data-no-assistant], [data-testid="nav-search"], header, nav';
    const isEntry = (el) => {
      if (!el || !el.matches) return false;
      if (!el.matches("input, select, textarea, [contenteditable='true']")) return false;
      if (el.type === "hidden" || el.disabled || el.readOnly) return false;
      if (el.closest(SKIP)) return false;
      if (panelRef.current && panelRef.current.contains(el)) return false;
      return true;
    };

    const settle = (cell) => {
      const btn = ballRef.current;
      if (!btn || !cell) return;
      // Anchor by ROW where there is one, else by the field itself — that is
      // what lets dialogs and card forms (no <tr> anywhere) be followed too.
      const row = cell.closest("tr, [role='row']") || cell;
      const rowR = row.getBoundingClientRect();
      const cellR = cell.getBoundingClientRect();
      if (!cellR.width || !cellR.height) return;   // field not actually visible

      // The ball's own box comes from LAYOUT (offsetWidth/Height + the fixed
      // bottom-5/right-5 anchor), never from getBoundingClientRect: the rect
      // reflects whatever transform is live — the rove keyframes' -42px, or
      // the rotated bounding box mid-roll — so measuring it made every landing
      // miss by that offset. Layout is transform-proof.
      const bw = btn.offsetWidth, bh = btn.offsetHeight;
      const bRight = document.documentElement.clientWidth - 20;
      const bLeft = bRight - bw;
      const homeCy = window.innerHeight - 20 - bh / 2;
      const half = bh / 2 + 6;
      let targetCy = rowR.top + rowR.height / 2;

      /* DODGE — never sit on top of, or alongside, the line being keyed. The
         ball is pinned to the right edge so it can only move vertically.

         This used to test the focused CELL's box. On a full-width sheet the
         cell you are typing in sits mid-table, so the test said "no overlap"
         and the ball parked LEVEL with the row — which put it straight onto
         that row's right-hand cells, right beside the cursor. What matters is
         whether the ROW reaches into the ball's column, which on a wide sheet
         it always does. Testing the row (union'd with the cell, for dialogs
         and card forms where there is no row) is what actually keeps it off
         the line being worked on. */
      const lineLeft = Math.min(rowR.left, cellR.left);
      const lineRight = Math.max(rowR.right, cellR.right);
      const overlapsBall = lineRight > bLeft - GAP && lineLeft < bRight + GAP;
      if (overlapsBall) {
        const above = rowR.top - GAP - bh / 2;
        const below = rowR.bottom + GAP + bh / 2;
        targetCy = above >= half ? above : below;
      }
      // keep it fully on screen whatever the row's position
      const wantCy = Math.min(Math.max(targetCy, half), window.innerHeight - half);
      const dy = wantCy - homeCy;
      // ROUND DF — the ball still rolls level with the row, but the eyes no
      // longer aim at the cell: the sockets stay put and only blink.
      dyRef.current = dy;
      clearTimeout(homeRef.current);   // cancel any pending go-home
      setFollow({ dy });
    };
    const onFocusIn = (e) => {
      if (!isEntry(e.target)) return;
      stopRoll();          // being led to a field outranks the section-change trip
      settle(e.target);
    };
    const onFocusOut = () => {
      // let a sibling cell take focus first; only go home if nothing did
      setTimeout(() => {
        if (isEntry(document.activeElement)) return;
        dyRef.current = 0;
        // Roll back to dy 0 WITH the following class still on, then drop the
        // class once it has arrived — dropping it immediately would restart
        // the rove keyframes and teleport the ball home.
        setFollow({ dy: 0 });
        clearTimeout(homeRef.current);
        homeRef.current = setTimeout(() => setFollow(null), 680);
      }, 90);
    };
    const reflow = () => {
      if (isEntry(document.activeElement)) settle(document.activeElement);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("scroll", reflow, true);
    window.addEventListener("resize", reflow);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("scroll", reflow, true);
      window.removeEventListener("resize", reflow);
      clearTimeout(homeRef.current);
    };
  }, []);


  useEffect(() => {
    if (!unitId) return;
    let dead = false;
    setData(null);
    setHealth(null);
    api.get(`/inventory/negative-issues?unit_id=${unitId}`)
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => { if (!dead) setData({ available: false, issues: [] }); });
    return () => { dead = true; };
  }, [unitId]);

  // LIVE on open: every time the panel is opened, force a fresh ledger scan
  // (force=true bypasses the backend's 10-minute scan throttle; noCache
  // bypasses the frontend's short GET cache). A correction made a minute ago
  // must be reflected the moment the assistant is opened — never stale data.
  useEffect(() => {
    if (!open || !unitId) return;
    let dead = false;
    setHealth(null);   // health view refetches fresh too when visited
    setShownIssues(15);   // fresh open starts from the top of the list again
    api.get(`/inventory/negative-issues?unit_id=${unitId}&force=true`, { noCache: true })
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, [open, unitId]);

  // Factory Health sources — fetched once, on first open of the health view.
  // Each source fails independently to null => "Data unavailable" for its rows.
  useEffect(() => {
    if (view !== "health" || health !== null || !unitId) return;
    let dead = false;
    const grab = (url) => api.get(url, { noCache: true }).then((r) => r.data).catch(() => null);
    Promise.all([
      grab(`/dashboard/stats?unit_id=${unitId}`),
      grab(`/rework/pool?unit_id=${unitId}`),
      grab(`/jobwork/snapshot?unit_id=${unitId}`),
      grab(`/planning/fg-snapshot?unit_id=${unitId}`),
    ]).then(([stats, rework, jobwork, fg]) => {
      if (!dead) setHealth({ stats, rework, jobwork, fg });
    });
    return () => { dead = true; };
  }, [view, health, unitId]);

  // click outside closes the panel (and resets to the menu)
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
        setView("menu");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const issues = data?.issues || [];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const todayLabel = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const fmt = (x) => Number(x || 0).toLocaleString("en-IN");

  const solutionFor = (it) => it.responsible_role === "inspection"
    ? [
        `Open Part Flow and locate the ${it.operation} row — its bin shows exactly how many pieces are unaccounted for.`,
        `Physically count the pieces waiting for ${it.operation} and enter the count in Inventory → Openings (dated the morning of the count).`,
        "Compare the previous stage's good output with what this inspection recorded — look for a day where inspected exceeds available.",
        "Check rejected quantities and remove any duplicate or double-keyed inspection entry on the quality sheet.",
        "After the recount and corrections, this issue resolves automatically once the balance is no longer negative.",
      ]
    : [
        `Open Part Flow and locate the ${it.operation} row — the negative closing is the gap to explain.`,
        `Physically count the stock lying before ${it.operation} and enter it in Inventory → Openings (dated the morning of the count).`,
        `Verify the previous operation's production entries — a missing or under-keyed entry there is the most common cause.`,
        `Check ${it.operation}'s own entries for an over-keyed quantity on any date.`,
        "After the recount and corrections, this issue resolves automatically once the balance is no longer negative.",
      ];

  /* ---- Factory Health rows, each built from an EXISTING module's figures ---- */
  const healthRows = (() => {
    if (!health) return [];
    const { stats, rework, jobwork, fg } = health;

    const prodPct = stats?.yesterday?.efficiency_percent;
    const negCount = data?.available ? issues.length : null;
    const reworkBal = rework?.totals?.balance;
    const jwRows = Array.isArray(jobwork) ? jobwork : jobwork?.rows || null;
    const jwOutside = jwRows ? jwRows.reduce((s, r) => s + Number(r.closing || 0), 0) : null;
    const fgRows = fg?.rows || null;
    const fgCritical = fgRows ? fgRows.filter((r) => r.criticality === "critical").length : null;
    const fgStale = fgRows ? fgRows.filter((r) => r.criticality === "stale").length : null;

    const dot = (level) => (level === 0 ? "🟢" : level === 1 ? "🟠" : "🔴");
    return [
      {
        key: "production", label: "Production",
        value: prodPct != null ? `${prodPct}%` : null,
        status: prodPct == null ? null : dot(prodPct >= 90 ? 0 : prodPct >= 70 ? 1 : 2),
        what: prodPct != null
          ? `Yesterday's press-shop stroke efficiency was ${prodPct}% (${fmt(stats?.yesterday?.total_production_strokes)} of ${fmt(stats?.yesterday?.total_target_strokes)} target strokes).`
          : null,
        next: "Open the Production Sheet to see which parts/machines ran below target and where entries are missing.",
        go: () => navigate("/production-sheet"),
        goLabel: "View Production →",
      },
      {
        key: "inventory", label: "Inventory",
        value: negCount != null ? `${negCount} negative${negCount === 1 ? "" : "s"}` : null,
        status: negCount == null ? null : dot(negCount === 0 ? 0 : negCount <= 5 ? 1 : 2),
        what: negCount != null
          ? `${negCount} part+operation combinations show negative stock — more output recorded than material available. The worst first, with step-by-step fixes, are in Inventory Issues.`
          : null,
        next: "Each issue lists the part, operation and quantity, plus the recount/entry checks to run.",
        go: () => setView("issues"),
        goLabel: "View Issues →",
      },
      {
        key: "rework", label: "Rework",
        value: reworkBal != null ? `${fmt(reworkBal)} pcs` : null,
        status: reworkBal == null ? null : dot(reworkBal === 0 ? 0 : reworkBal < 5000 ? 1 : 2),
        what: reworkBal != null
          ? `${fmt(reworkBal)} pieces are waiting in the rework pool across ${fmt(rework?.rows?.length)} part${(rework?.rows?.length || 0) === 1 ? "" : "s"} — outside normal stock until processed.`
          : null,
        next: "Open Rework and use Process on the biggest balances: recover pieces back into production or scrap them to Red Tag.",
        go: () => navigate("/rework"),
        goLabel: "View Rework →",
      },
      {
        key: "jobwork", label: "Jobwork",
        value: jwOutside != null ? `${fmt(jwOutside)} pcs outside` : null,
        status: jwOutside == null ? null : dot(jwOutside === 0 ? 0 : 1),
        what: jwOutside != null
          ? `${fmt(jwOutside)} pieces are currently at outside suppliers (platers) and not yet received back.`
          : null,
        next: "Open Job Work to see per-supplier balances; use each row's 'sent history' to audit what went out and when.",
        go: () => navigate("/jobwork"),
        goLabel: "View Jobwork →",
      },
      {
        key: "fg", label: "FG",
        value: fgCritical != null ? (fgCritical === 0 ? "OK" : `${fgCritical} critical`) : null,
        status: fgCritical == null ? null : dot(fgCritical === 0 ? 0 : fgCritical <= 10 ? 1 : 2),
        what: fgCritical != null
          ? `${fgCritical} FG-tracked part${fgCritical === 1 ? " is" : "s are"} below the days-of-cover threshold against the monthly schedule.`
          : null,
        next: "Open FG Planning — critical parts are listed worst-first with current FG, daily requirement and days of cover.",
        go: () => navigate("/planning"),
        goLabel: "View FG Planning →",
      },
      {
        key: "stuck", label: "Stuck Parts",
        value: fgStale != null ? `${fgStale}` : null,
        status: fgStale == null ? null : dot(fgStale === 0 ? 0 : 2),
        what: fgStale != null
          ? `${fgStale} part${fgStale === 1 ? " has" : "s have"} had no dispatch movement for 14+ days (marked "stale" in FG Planning) — stock sitting without moving.`
          : null,
        next: "Open FG Planning and filter the stale chip — check whether the schedule ended, dispatches stopped being logged, or the stock is genuinely stuck.",
        go: () => navigate("/planning"),
        goLabel: "View FG Planning →",
      },
    ];
  })();

  if (!unitId) return null;   // no unit selected (e.g. unit picker) — hide

  const headerTitle = view === "issues" ? "Inventory Issues" : view === "health" ? "Factory Health"
    : view === "where" ? "Where Is My Part?" : view === "forecast" ? "RM Demand Forecast" : "Factory Assistant";

  // Portal to document.body: pinned to the viewport bottom-right on every page.
  return createPortal(
    /* assistant-portal is a hook for @media print. This whole subtree is
       portalled onto <body>, OUTSIDE #root, so the print rules that hide the
       app by selecting #root never touched it — the ball printed onto page 1
       of every sheet, badge and all. */
    <div ref={panelRef} className="assistant-portal fixed bottom-5 right-5 z-[80] flex flex-col items-end gap-2">
      {open && (
        <div
          className={`${view === "where" ? "w-[400px]" : "w-[340px]"} max-w-[calc(100vw-2.5rem)] rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden`}
          data-testid="inventory-assistant-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-muted/40">
            <p className="text-sm font-semibold flex items-center gap-2">
              {view !== "menu" && (
                <button type="button" className="text-muted-foreground hover:text-foreground -ml-1"
                        onClick={() => setView("menu")} aria-label="Back" data-testid="assistant-back">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <MessageCircle className="w-4 h-4 text-primary" /> {headerTitle}
            </p>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => { setOpen(false); setView("menu"); }}
              aria-label="Close"
              data-testid="assistant-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className={`${view === "where" ? "max-h-[520px]" : "max-h-[420px]"} overflow-y-auto p-4 space-y-3 text-sm`}>

            {/* ================= MENU ================= */}
            {view === "menu" && (
              <>
                <p className="text-xs text-muted-foreground">🤖 {greeting}. What would you like to check?</p>
                <button type="button"
                        className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 hover:bg-muted/60 p-3 text-left transition-colors"
                        onClick={() => setView("issues")} data-testid="assistant-menu-issues">
                  <Boxes className="w-5 h-5 text-amber-600 shrink-0" />
                  <span>
                    <span className="text-xs font-semibold block">📦 Inventory Issues</span>
                    <span className="text-[11px] text-muted-foreground">
                      {data?.available ? `${issues.length} negative-stock issue${issues.length === 1 ? "" : "s"} for you` : "negative-stock guide"}
                    </span>
                  </span>
                </button>
                <button type="button"
                        className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 hover:bg-muted/60 p-3 text-left transition-colors"
                        onClick={() => setView("health")} data-testid="assistant-menu-health">
                  <Factory className="w-5 h-5 text-primary shrink-0" />
                  <span>
                    <span className="text-xs font-semibold block">🏭 Factory Health</span>
                    <span className="text-[11px] text-muted-foreground">one-glance morning summary of every module</span>
                  </span>
                </button>
                <button type="button"
                        className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 hover:bg-muted/60 p-3 text-left transition-colors"
                        onClick={() => setView("where")} data-testid="assistant-menu-where">
                  <Search className="w-5 h-5 text-sky-600 shrink-0" />
                  <span>
                    <span className="text-xs font-semibold block">🔎 Where Is My Part?</span>
                    <span className="text-[11px] text-muted-foreground">trace a part's pieces — RM to dispatch</span>
                  </span>
                </button>
                <button type="button"
                        className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 hover:bg-muted/60 p-3 text-left transition-colors"
                        onClick={() => setView("forecast")} data-testid="assistant-menu-forecast">
                  <Boxes className="w-5 h-5 text-violet-600 shrink-0" />
                  <span>
                    <span className="text-xs font-semibold block">📦 RM Demand Forecast</span>
                    <span className="text-[11px] text-muted-foreground">how much material next 30 days — gaps &amp; what-ifs</span>
                  </span>
                </button>
              </>
            )}

            {/* ================= RM FORECAST ================= */}
            {view === "forecast" && <RmForecastView unitId={unitId} />}

            {/* ================= WHERE IS MY PART ================= */}
            {view === "where" && <WhereIsMyPartTrace unitId={unitId} />}

            {/* ================= FACTORY HEALTH ================= */}
            {view === "health" && (
              <>
                <p className="text-xs font-semibold uppercase">{greeting} 👋</p>
                <p className="text-xs text-muted-foreground">Factory Health — {todayLabel}</p>
                {health === null ? (
                  <p className="text-xs text-muted-foreground">Checking all modules…</p>
                ) : (
                  healthRows.map((row) => (
                    <details key={row.key} className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2"
                             data-testid={`health-${row.key}`}>
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-2">
                        <span className="text-xs font-medium">{row.status || "⚪"} {row.label}</span>
                        <span className={`text-xs font-semibold tabular-nums ${row.status === "🔴" ? "text-red-600" : row.status === "🟠" ? "text-amber-600" : ""}`}>
                          {row.value != null ? row.value : "Data unavailable"}
                        </span>
                      </summary>
                      <div className="mt-2 pt-2 border-t border-border/40 space-y-1.5">
                        {row.what ? (
                          <>
                            <p className="text-[11px] leading-snug">{row.what}</p>
                            <p className="text-[11px] text-muted-foreground leading-snug">{row.next}</p>
                            <Button size="sm" variant="outline" className="h-6 text-[11px] rounded-lg"
                                    onClick={row.go} data-testid={`health-go-${row.key}`}>
                              {row.goLabel}
                            </Button>
                          </>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">
                            Data unavailable — this module could not be read (it may need a permission you don't have).
                          </p>
                        )}
                      </div>
                    </details>
                  ))
                )}
              </>
            )}

            {/* ================= INVENTORY ISSUES (existing feature, unchanged) ================= */}
            {view === "issues" && (
              data === null ? (
                <p className="text-muted-foreground text-xs">Checking inventory…</p>
              ) : !data.available ? (
                <p className="text-muted-foreground text-xs">Inventory check is currently unavailable.</p>
              ) : issues.length === 0 ? (
                <p className="text-xs">✅ No negative inventory issues found.</p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    {greeting}. I found <b>{issues.length}</b> inventory issue{issues.length === 1 ? "" : "s"} that may need your attention.
                  </p>
                  {issues.slice(0, shownIssues).map((it) => (
                    <details
                      key={`${it.part_id}-${it.operation_id}`}
                      className={`rounded-xl border p-3 ${it.important
                        ? "border-red-300 dark:border-red-900 bg-red-50/60 dark:bg-red-950/20"
                        : "border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20"}`}
                      data-testid={`assistant-issue-${it.part_no}-${it.operation}`}
                    >
                      <summary className="cursor-pointer list-none space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold">
                            ⚠ {it.operation}
                            {it.important && (
                              <span className="ml-1.5 rounded-full bg-red-600 text-white px-1.5 py-0.5 text-[9px] font-bold align-middle">IMPORTANT</span>
                            )}
                          </p>
                          <p className="text-xs font-bold text-red-600 tabular-nums whitespace-nowrap">
                            {Number(it.negative_qty).toLocaleString("en-IN")} pcs
                          </p>
                        </div>
                        <p className="text-[11px] leading-snug">
                          <span className="font-medium">{it.part_name || "—"}</span>
                          {it.part_no ? <span className="text-muted-foreground"> · {it.part_no}</span> : null}
                        </p>
                        <p className="text-[10px] text-muted-foreground">Click for the solution ▾</p>
                      </summary>
                      <div className="mt-2 pt-2 border-t border-border/40 space-y-1.5">
                        <p className="text-[11px] font-semibold">Solution — step by step:</p>
                        <ol className="list-decimal ml-4 space-y-1 text-[11px] text-muted-foreground leading-snug">
                          {solutionFor(it).map((step, i) => <li key={i}>{step}</li>)}
                        </ol>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[11px] rounded-lg"
                          onClick={() => navigate(`/inventory?part=${it.part_id}`)}
                          data-testid={`assistant-view-flow-${it.part_no}`}
                        >
                          View Part Flow
                        </Button>
                      </div>
                    </details>
                  ))}
                  {issues.length > shownIssues && (
                    <div className="text-center space-y-1">
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={() => setShownIssues((n) => n + 15)}
                        data-testid="assistant-see-more">
                        See more ({issues.length - shownIssues} remaining)
                      </Button>
                      <p className="text-[10px] text-muted-foreground">
                        or open Inventory for the full picture.
                      </p>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        </div>
      )}
      <button
        ref={ballRef}
        type="button"
        /* ROUND DI — it flies almost all the time now, so it has to stay
           CATCHABLE. Reaching for it stops it where it is (the CSS pause only
           ever covered the old keyframe animations, not the flight), and
           clicking it calls the whole flight off so the ball is back on its
           home spot where the panel opens. */
        onPointerEnter={() => animsRef.current.forEach((a) => { try { a.pause(); } catch {} })}
        onPointerLeave={() => animsRef.current.forEach((a) => { try { a.play(); } catch {} })}
        onClick={() => { stopRoll(); setOpen((o) => !o); }}
        className={`assistant-rover${follow ? " assistant-following" : ""}${
          flying && !follow ? " assistant-flying" : ""}`}
        /* The travel itself lives here: translateY carries the ball level with
           the row, and the rotation (whole turns only, so it always lands
           upright) makes the trip read as a roll down the edge of the sheet
           instead of a slide. Without this transform the ball never moved —
           dy was measured and then thrown away. */
        style={follow
          ? {
              /* ROUND CS — `scale` is its own property, so the size the roll
                 chose for this page composes with the follow transform
                 instead of overwriting it. */
              scale: ballScale,
              transform: `translateY(${follow.dy}px) rotate(${Math.round(follow.dy / 220) * 360}deg)`,
            }
          : { scale: ballScale }}
        title="Factory Assistant"
        aria-label="Open Factory Assistant"
        /* Out of the tab order while it is following, so tabbing from the
           last field of a form never lands on the ball mid-entry. It goes
           back to being keyboard-reachable the moment it rolls home. */
        tabIndex={follow ? -1 : 0}
        data-testid="inventory-assistant-btn"
      >
        <span className="assistant-ball">
          {/* ROUND DF — the sockets no longer move. .eye is now a fixed
              position only; the inner .pupil keeps the blink (scaleY), which
              is the one bit of eye motion that stays — a frozen stare reads
              as switched off. */}
          <span className="eye eye-l" aria-hidden="true"><i className="pupil" /></span>
          <span className="eye eye-r" aria-hidden="true"><i className="pupil" /></span>
          {data?.available && issues.length > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="inventory-assistant-badge"
            >
              {issues.length > 99 ? "99+" : issues.length}
            </span>
          )}
        </span>
      </button>
    </div>,
    document.body
  );
}

/* 📦 RM Demand Forecast view — deterministic material table + AI Q&A on top.
 * All numbers come from GET /inventory/rm-forecast (server-computed, cached);
 * the LLM only explains those rows (POST .../ai). Read-only everywhere. */
function RmForecastView({ unitId }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [chat, setChat] = useState([]);        // [{role:'user'|'ai', text}]
  const [asking, setAsking] = useState(false);
  const days = 30;

  useEffect(() => {
    let dead = false;
    setLoading(true);
    api.get(`/inventory/rm-forecast?unit_id=${unitId}&days=${days}`)
      .then((r) => { if (!dead) setRows(r.data); })
      .catch(() => { if (!dead) setRows(null); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question || asking) return;
    setChat((c) => [...c, { role: "user", text: question }]);
    setQ("");
    setAsking(true);
    try {
      const { data } = await api.post(`/inventory/rm-forecast/ai`,
        { unit_id: unitId, question, days });
      setChat((c) => [...c, { role: "ai", text: data.answer }]);
    } catch (e) {
      setChat((c) => [...c, { role: "ai", text: e?.response?.data?.detail || "Forecast AI failed — try again." }]);
    } finally { setAsking(false); }
  };

  const fmt = (v, u) => v == null ? "—" : `${Number(v).toLocaleString("en-IN")} ${u || ""}`.trim();
  const gaps = (rows?.materials || []).filter((m) => (m.projected_shortage || 0) > 0);

  return (
    <div className="space-y-3">
      {loading ? (
        <p className="text-xs text-muted-foreground">Calculating from live ERP data…</p>
      ) : !rows ? (
        <p className="text-xs text-muted-foreground">Forecast unavailable right now.</p>
      ) : (
        <>
          <p className="text-[11px] text-muted-foreground">
            Next {rows.horizon_days ?? days} days ({rows.period}) · schedule + recent consumption · forecast only, nothing is changed.
          </p>
          {gaps.length === 0 ? (
            <p className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-2">
              🟢 Forecast indicates sufficient stock for every tracked material.
            </p>
          ) : gaps.slice(0, 5).map((m2, i) => (
            <div key={i} className="rounded-lg border border-border/60 p-2 text-[11px] leading-5"
                 data-testid={`rmf-gap-${i}`}>
              <p className="font-semibold">⚠️ {m2.material}{m2.shared_pool ? " · shared pool" : ""}</p>
              <p>
                Demand ≈<b>{fmt(m2.forecast_demand, m2.unit)}</b> · stock {fmt(m2.current_stock, m2.unit)}
                {" "}· gap <b className="text-red-600">≈{fmt(m2.projected_shortage, m2.unit)}</b>
                {" "}· confidence <span title={m2.reason || ""}>{m2.confidence}</span>
              </p>
              {/* ROUND BX — days of cover: stock ÷ (forecast demand ÷ horizon).
                  Answers "how long before this coil runs out?" straight from the
                  forecast's own two numbers; no new model, nothing stored. */}
              {(() => {
                const horizon = Number(rows.horizon_days || days || 30);
                const perDay = Number(m2.forecast_demand || 0) / Math.max(1, horizon);
                if (!(perDay > 0)) return null;
                const dc = Number(m2.current_stock || 0) / perDay;
                const tone = dc <= 3 ? "text-red-600" : dc <= 7 ? "text-orange-600" : "text-emerald-700";
                return (
                  <p title={`${fmt(m2.current_stock, m2.unit)} in stock ÷ ${perDay.toLocaleString("en-IN", { maximumFractionDigits: 1 })} ${m2.unit}/day (demand ${fmt(m2.forecast_demand, m2.unit)} ÷ ${horizon} days) = ${dc.toFixed(1)} days`}>
                    burn ≈<b>{perDay.toLocaleString("en-IN", { maximumFractionDigits: 1 })} {m2.unit}/day</b>
                    {" "}· <b className={tone}>≈{dc.toFixed(1)} days of cover left</b>
                    {dc <= 7 && <span className="ml-1">⏰ order now</span>}
                  </p>
                );
              })()}
              {(m2.contributing_parts || []).length > 0 && (
                <p className="text-muted-foreground">
                  top: {m2.contributing_parts.slice(0, 3).map((c2) => `${c2.part_no} (${fmt(c2.demand_kg, m2.unit)})`).join(" · ")}
                </p>
              )}
            </div>
          ))}
          <div className="flex flex-wrap gap-1.5">
            {["How much RM will we need next month?",
              "Which RM may become short?",
              "What if production increases 20%?"].map((s) => (
              <button key={s} type="button"
                      className="text-[10px] rounded-full border border-border/60 px-2 py-1 hover:bg-muted/60"
                      onClick={() => ask(s)} disabled={asking}>
                {s}
              </button>
            ))}
          </div>
          {chat.map((msg, i) => (
            <div key={i} className={`text-xs rounded-lg p-2 whitespace-pre-wrap ${
              msg.role === "user" ? "bg-primary/10 ml-6" : "bg-muted/50 mr-2"}`}>
              {msg.text}
            </div>
          ))}
          {asking && <p className="text-[11px] text-muted-foreground">🤖 analyzing forecast rows…</p>}
          <div className="flex gap-1.5">
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
                   placeholder='Ask… e.g. "SS COIL 304 kitna lagega?"'
                   className="flex-1 h-8 text-xs rounded-md border border-input bg-background px-2"
                   data-testid="rmf-question" />
            <Button size="sm" className="h-8" disabled={asking || !q.trim()} onClick={() => ask()}>Ask</Button>
          </div>
        </>
      )}
    </div>
  );
}
